import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useUploadStore } from "src/api/useUploadStore";
import ConfirmationPopover from "src/components/ConfirmationPopover";

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
  return `${value} ${sizes[i]}`;
}

export default function RequestTable() {
  const uploads = useUploadStore((state) => state.uploads);
  const { t } = useTranslation();
  return (
    <TableContainer>
      <Table aria-label="request table">
        <TableHead>
          <TableRow>
            <TableCell>{t("requestTable.collectEventName")}</TableCell>
            <TableCell align="right">{t("requestTable.filename")}</TableCell>
            <TableCell align="center">{t("requestTable.status")}</TableCell>
            <TableCell align="right">{t("requestTable.totalSize")}</TableCell>
            <TableCell align="right">{t("requestTable.started")}</TableCell>
            <TableCell align="right">{t("requestTable.totalTime")}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(uploads).length ? (
            Object.entries(uploads).map(([id, row]) => (
              <TableRow
                key={id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell component="th" scope="row">
                  {row.collectEventName}
                </TableCell>
                <TableCell align="right">{row.filename}</TableCell>
                <TableCell sx={{ maxWidth: 250 }}>
                  {(row.progress || 0) <= 100 ? (
                    <Stack style={{ alignItems: "center" }}>
                      <Box style={{ width: "100%" }}>
                        <LinearProgress
                          variant="determinate"
                          color={
                            row.isError
                              ? "error"
                              : row.progress === 100
                              ? "success"
                              : undefined
                          }
                          value={row.progress ?? 0}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >{`${row.progress ?? 0}%`}</Typography>
                      {row.progress === 100 && !row.isError
                        ? t("requestTable.notifiedByEmail")
                        : row.statusMessage}
                    </Stack>
                  ) : (
                    row.statusMessage
                  )}
                </TableCell>
                <TableCell align="right">{formatBytes(row.fileSize)}</TableCell>
                <TableCell align="right">{row.startDate}</TableCell>
                <TableCell align="right">{row.elapsedTime}</TableCell>
                <TableCell align="right">
                  <>
                    <ConfirmationPopover
                      buttonLabel="Cancel"
                      buttonProps={{
                        disabled:
                          row.progress === 100 || row.isError || !row.progress,
                      }}
                      confirmText="Cancel upload"
                      onConfirm={() => {
                        row.abortController
                          ? row.abortController.abort()
                          : null;
                      }}
                    />
                  </>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                sx={{ textAlign: "center" }}
                colSpan={6}
                component="th"
                scope="row"
              >
                {t("requestTable.noData")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
